import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Row, Col, Spin, Typography, notification, Button, Drawer, Badge } from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import { BASE_URL, getProductsByProjectId, addOrRemoveCart, getCart, createCheckoutSession } from "../../api";
import "./ProductListing.scss";

const { Meta } = Card;
const { Title } = Typography;

// ✅ Placeholder Image (Used when no background is available)
const PLACEHOLDER_IMAGE = "https://via.placeholder.com/300x200?text=No+Image";

const ProductListing = () => {
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('projectId');
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState(null); // Stores Cart Data
    const [loading, setLoading] = useState(true);
    const [cartVisible, setCartVisible] = useState(false);
    const [buttonLoading, setButtonLoading] = useState({});

    useEffect(() => {
        let isMounted = true; // Prevent memory leaks

        const fetchProducts = async () => {
            try {
                const response = await getProductsByProjectId(projectId);
                if (isMounted && response.data && response.data.products) {
                    setProducts(response.data.products);
                }
            } catch (error) {
                console.error("Error fetching products:", error);
                notification.error({ message: "Failed to load products!" });
            }
            if (isMounted) setLoading(false);
        };

        fetchProducts();
        fetchCart();

        return () => {
            isMounted = false; // Cleanup function
        };
    }, [projectId]);

    // ✅ Fetch Cart
    const fetchCart = async () => {
        try {
            const response = await getCart();
            if (response.data) {
                setCart(response.data); // No need for `.products`, it already contains them
            }
        } catch (error) {
            console.error("Error fetching cart:", error);
        }
    };

    // ✅ Handle Add/Remove Cart
    const handleCartAction = async (productId, isInCart) => {
        setButtonLoading((prev) => ({ ...prev, [productId]: true }));

        const quantity = isInCart ? 0 : 1; // 1 for Add, 0 for Remove

        try {
            await addOrRemoveCart(productId, quantity);
            notification.success({ message: isInCart ? "Removed from cart!" : "Added to cart!" });

            fetchCart(); // Refresh Cart Data
        } catch (error) {
            console.error("Error updating cart:", error);
            notification.error({ message: "Failed to update cart!" });
        }

        setButtonLoading((prev) => ({ ...prev, [productId]: false }));
    };

    // ✅ Handle Checkout
    const handleCheckout = async () => {
        if (!cart || !cart._id) return notification.error({ message: "No active cart found!" });

        try {
            const response = await createCheckoutSession(cart._id);
            if (response.data?.session?.url) {
                window.location.href = response.data.session.url;
            }
        } catch (error) {
            console.error("Error starting checkout:", error);
            notification.error({ message: "Failed to initiate checkout!" });
        }
    };

    return (
        <div className="product-listing-container">
            <Title level={2}>Project Products</Title>

            {loading ? (
                <div className="loading-container">
                    <Spin size="large" />
                </div>
            ) : (
                <Row gutter={[16, 16]}>
                    {products.length === 0 ? (
                        <Col span={24}>
                            <p>No products found for this project.</p>
                        </Col>
                    ) : (
                        products.map((product) => {
                            const backgroundImage = product.links?.find((link) => link.type === "background")?.link;
                            const imageUrl = backgroundImage ? `${BASE_URL}${product?.imagesPath}/${backgroundImage}` : PLACEHOLDER_IMAGE;

                            // ✅ New isInCart logic (Cart response has products directly, no nested productId)
                            const isInCart = cart?.products?.some((item) => item._id === product._id);

                            return (
                                <Col key={product._id} xs={24} sm={12} md={8} lg={6}>
                                    <Card
                                        hoverable
                                        cover={<img alt={product.name} src={imageUrl} className="product-card-image" loading="lazy" />}
                                        className="compact-product-card"
                                    >
                                        <Meta title={product.name} description={`$${product.price}`} />
                                        <p className="product-description">{product.description}</p>

                                        {/* ✅ Add to Cart / Remove from Cart Button */}
                                        <Button
                                            type={isInCart ? "default" : "primary"}
                                            onClick={() => handleCartAction(product._id, isInCart)}
                                            loading={buttonLoading[product._id]}
                                            block
                                            className="cart-button"
                                        >
                                            {isInCart ? "Remove from Cart" : "Add to Cart"}
                                        </Button>
                                    </Card>
                                </Col>
                            );
                        })
                    )}
                </Row>
            )}

            {/* ✅ Floating Cart Button */}
            <div className="floating-cart-container">
                <Badge count={cart?.products?.length || 0} offset={[-5, 5]} className="cart-badge">

                </Badge>
                <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        icon={<ShoppingCartOutlined />}
                        className="floating-cart-button"
                        onClick={() => setCartVisible(true)}
                    />
            </div>

            {/* ✅ Cart Drawer */}
            <Drawer
                title="Your Cart"
                placement="right"
                closable
                onClose={() => setCartVisible(false)}
                open={cartVisible}
                width={400}
            >
                {cart?.products?.length ? (
                    <>
                        {cart.products.map((item) => (
                            <div key={item._id} className="cart-item">
                                <img
                                    src={
                                        item.links?.find((link) => link.type === "background")?.link
                                            ? `${BASE_URL}${item?.imagesPath}/${item.links.find((link) => link.type === "background").link}`
                                            : PLACEHOLDER_IMAGE
                                    }
                                    alt={item.name}
                                    className="cart-item-image"
                                />
                                <div className="cart-item-details" style={{ marginLeft: 10 }}>
                                    <p className="cart-item-name">{item.name}</p>
                                    <p className="cart-item-price">${item.price} x {item.quantity}</p>
                                </div>
                            </div>
                        ))}
                        <div className="cart-total">
                            <p>Total: ${cart.products.reduce((sum, item) => sum + item.quantity * item.price, 0)}</p>
                        </div>
                        <Button type="primary" block onClick={handleCheckout}>
                            Checkout
                        </Button>
                    </>
                ) : (
                    <p>Your cart is empty.</p>
                )}
            </Drawer>
        </div>
    );
};

export default ProductListing;
